import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { ArrowDownRight, ArrowUpRight, ArrowLeft, Calendar, ChevronLeft, ChevronRight, Inbox } from 'lucide-react';
import Swal from 'sweetalert2';
import api from '../api';
import './Wallet.css';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ViewAllActivity() {
  const navigate = useNavigate();
  const { seller } = useSelector((s: RootState) => s.auth);

  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [filterType, setFilterType] = useState('ALL');
  const itemsPerPage = 10;

  useEffect(() => {
    if (seller?._id) {
      api.get(`/seller-wallet/${seller._id}`)
        .then(res => {
          if (res.data.success) {
            const rawTxs = res.data.data.transactions || [];
            
            const formatted = rawTxs.map((t: any) => {
              let comm = t.commissionDeducted || 0;
              let gst = t.gstDeducted || 0;
              let tcs = 0;
              let platformFee = 0;
              let sms = 0;
              let pgFee = 0;
              let totalDeduction = t.commission_GST_Deducted || 0;
              let desc = t.description || 'Transaction';
              
              if (desc && (desc.includes('[Comm:') || desc.includes('(Comm:'))) {
                const commMatch = desc.match(/Comm:\s*₹?(\d+(\.\d+)?)/);
                const gstMatch = desc.match(/GST:\s*₹?(\d+(\.\d+)?)/);
                const tcsMatch = desc.match(/TCS:\s*₹?(\d+(\.\d+)?)/);
                const platformFeeMatch = desc.match(/Platform Fee:\s*₹?(\d+(\.\d+)?)/);
                const smsMatch = desc.match(/SMS:\s*₹?(\d+(\.\d+)?)/);
                const pgFeeMatch = desc.match(/PG Fee:\s*₹?(\d+(\.\d+)?)/);
                
                if (commMatch && comm === 0) comm = parseFloat(commMatch[1]);
                if (gstMatch && gst === 0) gst = parseFloat(gstMatch[1]);
                if (tcsMatch) tcs = parseFloat(tcsMatch[1]);
                if (platformFeeMatch) platformFee = parseFloat(platformFeeMatch[1]);
                if (smsMatch) sms = parseFloat(smsMatch[1]);
                if (pgFeeMatch) pgFee = parseFloat(pgFeeMatch[1]);
                
                if (totalDeduction === 0) {
                  totalDeduction = comm + gst + tcs + platformFee + sms + pgFee;
                }
              }

              let cleanDesc = desc.replace(/\s*[\[\(]Comm:.*?[\]\)]/i, '').trim();
              if (cleanDesc.includes('Wallet Topup')) {
                cleanDesc = 'Wallet Topup';
              }

              return {
                id: t._id,
                ref: cleanDesc,
                date: t.createdAt,
                amount: t.amount || 0,
                type: (t.transactionType || 'credit').toLowerCase(),
                status: (t.status || 'completed').toLowerCase(),
                orderId: t.orderId,
                productId: t.productId,
                commissionDeducted: comm,
                commission_GST_Deducted: totalDeduction,
                gstDeducted: gst,
                tcsDeducted: tcs,
                platformFee: platformFee,
                smsCharges: sms,
                pgFee: pgFee
              };
            });

            const sorted = [...formatted].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
            setAllTransactions(sorted);
          }
        })
        .catch(err => console.error('Error fetching wallet:', err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [seller]);

  const { availableMonths, grouped } = useMemo(() => {
    const groups: { [key: string]: any[] } = {};
    const monthsSet = new Set<string>();

    // Pre-populate last 6 months to ensure empty months can still be viewed
    const today = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthYear = d.toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
      monthsSet.add(monthYear);
      groups[monthYear] = [];
    }

    allTransactions.forEach(tx => {
      if (filterType !== 'ALL') {
        if (['CREDIT', 'DEBIT'].includes(filterType) && tx.type.toUpperCase() !== filterType) return;
        if (['PENDING', 'PROCESSING', 'FAILED', 'REFUNDED'].includes(filterType) && tx.status.toUpperCase() !== filterType) return;
      }
      const d = new Date(tx.date);
      const monthYear = d.toLocaleString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
      if (!groups[monthYear]) {
        groups[monthYear] = [];
        monthsSet.add(monthYear);
      }
      groups[monthYear].push(tx);
    });

    const sortedMonths = Array.from(monthsSet).sort((a, b) => {
      return new Date(b).getTime() - new Date(a).getTime();
    });

    return { availableMonths: sortedMonths, grouped: groups };
  }, [allTransactions, filterType]);

  const currentMonthString = availableMonths[currentMonthIndex];
  const currentMonthTransactions = currentMonthString ? grouped[currentMonthString] : [];

  const totalPages = Math.ceil(currentMonthTransactions.length / itemsPerPage);

  // Get current page transactions for the selected month
  const currentTransactions = currentMonthTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleOlderMonth = () => {
    if (currentMonthIndex < availableMonths.length - 1) {
      setCurrentMonthIndex(prev => prev + 1);
      setCurrentPage(1);
    }
  };

  const handleNewerMonth = () => {
    if (currentMonthIndex > 0) {
      setCurrentMonthIndex(prev => prev - 1);
      setCurrentPage(1);
    }
  };

  const getTxStyles = (tx: any) => {
    const status = (tx.status || 'pending').toLowerCase();
    const type = (tx.type || 'credit').toLowerCase();
    let amountClass = 'tx-amount-success';
    let statusClass = 'tx-status-completed';
    let sign = type === 'credit' ? '+' : '-';
    let Icon = type === 'credit' ? ArrowDownRight : ArrowUpRight;
    let iconWrapClass = type === 'credit' ? 'tx-credit-icon' : 'tx-debit-icon';

    let badgeText = 'CREDIT';

    if (status === 'pending' || status === 'processing') {
      amountClass = 'tx-amount-neutral';
      statusClass = 'tx-status-pending';
      badgeText = 'PROCESSING';
    } else if (status === 'failed') {
      amountClass = 'tx-amount-failed';
      statusClass = 'tx-status-failed';
      badgeText = 'FAILED';
    } else if (status === 'refunded') {
      amountClass = 'tx-amount-refunded';
      statusClass = 'tx-status-refunded';
      badgeText = 'REFUNDED';
    } else if (type === 'debit') {
      amountClass = 'tx-amount-failed';
      statusClass = 'tx-status-failed';
      badgeText = 'DEBIT';
    }
    
    return { amountClass, statusClass, sign, Icon, iconWrapClass, badgeText };
  };

  const showReceiptModal = (productName: string, productAmount: number, adminCommission: number, gstOnComm: number, tcs: number, platformFee: number, sms: number, pgFee: number, netSettlement: number, txDate: string, orderId: string, txType: string = 'credit') => {
    const isDebit = txType.toLowerCase() === 'debit';
    const totalFees = adminCommission + gstOnComm + tcs + platformFee + sms + pgFee;
    const grossTotal = netSettlement + totalFees;
    const receiptHtml = `
      <div style="text-align: left; font-family: monospace; background: #fff; padding: 20px; border-radius: 8px; border: 1px dashed #cbd5e1; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);">
        <div style="text-align: center; border-bottom: 2px dashed #94a3b8; padding-bottom: 10px; margin-bottom: 20px;">
          <h2 style="margin: 0; font-size: 20px; color: #0f172a;">SETTLEMENT RECEIPT</h2>
          <p style="margin: 5px 0 0; font-size: 12px; color: #64748b;">${txDate}</p>
          ${orderId ? `<p style="margin: 3px 0 0; font-size: 11px; color: #64748b; font-weight: 600;">Order #${orderId}</p>` : ''}
        </div>
        
        <div style="margin-bottom: 15px; font-size: 14px; color: #0f172a; font-weight: bold; text-align: center;">
          ${productName}
        </div>

        ${!isDebit ? `
        <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px;">
          <span style="color: #475569;">Gross Amount</span>
          <span style="font-weight: 600;">₹${grossTotal.toLocaleString('en-IN')}</span>
        </div>` : ''}
        ${adminCommission > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;">
          <span>Admin Commission</span>
          <span>-₹${adminCommission.toLocaleString('en-IN')}</span>
        </div>` : ''}
        ${gstOnComm > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;">
          <span>GST on Comm</span>
          <span>-₹${gstOnComm.toLocaleString('en-IN')}</span>
        </div>` : ''}
        ${tcs > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;"><span>TCS</span><span>-₹${tcs.toLocaleString('en-IN')}</span></div>` : ''}
        ${platformFee > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;"><span>Platform Fee</span><span>-₹${platformFee.toLocaleString('en-IN')}</span></div>` : ''}
        ${sms > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;"><span>SMS Charges</span><span>-₹${sms.toLocaleString('en-IN')}</span></div>` : ''}
        ${pgFee > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;"><span>PG Fee</span><span>-₹${pgFee.toLocaleString('en-IN')}</span></div>` : ''}
        
        <div style="border-top: 2px dashed #94a3b8; padding-top: 15px; display: flex; justify-content: space-between; font-size: 16px; font-weight: 700; color: ${isDebit ? '#ef4444' : '#0f766e'};">
          <span>${isDebit ? 'Total Deducted' : 'Net Settlement'}</span>
          <span>${isDebit ? '-' : ''}₹${(isDebit ? totalFees : netSettlement).toLocaleString('en-IN')}</span>
        </div>
        
        <div style="text-align: center; margin-top: 25px; font-size: 11px; color: #94a3b8;">
          Thank you for selling with DecsoPlus!
        </div>
      </div>
    `;

    Swal.fire({
      html: receiptHtml,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Close',
      cancelButtonText: 'Download PDF',
      confirmButtonColor: '#94a3b8',
      cancelButtonColor: '#6366f1',
      width: '400px',
      reverseButtons: true
    }).then((result) => {
      if (result.dismiss === Swal.DismissReason.cancel) {
        // Generate PDF
        const doc = new jsPDF();
        doc.setFont("Helvetica", "bold");
        doc.setFontSize(20);
        doc.setTextColor(15, 118, 110); // Teal
        doc.text("SETTLEMENT RECEIPT", 105, 20, { align: "center" });

        doc.setFontSize(10);
        doc.setFont("Helvetica", "normal");
        doc.setTextColor(100, 116, 139);
        doc.text(txDate, 105, 28, { align: "center" });
        if (orderId) doc.text(`Order #${orderId}`, 105, 34, { align: "center" });

        doc.setFontSize(14);
        doc.setFont("Helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        const splitTitle = doc.splitTextToSize(productName, 170);
        doc.text(splitTitle, 105, 48, { align: "center" });

        const tableBody: any[] = [];
        if (!isDebit) {
          tableBody.push(['Gross Amount', `Rs. ${grossTotal.toLocaleString('en-IN')}`]);
        }
        if (adminCommission > 0) tableBody.push(['Admin Commission', `-Rs. ${adminCommission.toLocaleString('en-IN')}`]);
        if (gstOnComm > 0) tableBody.push(['GST on Commission', `-Rs. ${gstOnComm.toLocaleString('en-IN')}`]);
        if (tcs > 0) tableBody.push(['TCS', `-Rs. ${tcs.toLocaleString('en-IN')}`]);
        if (platformFee > 0) tableBody.push(['Platform Fee', `-Rs. ${platformFee.toLocaleString('en-IN')}`]);
        if (sms > 0) tableBody.push(['SMS Charges', `-Rs. ${sms.toLocaleString('en-IN')}`]);
        if (pgFee > 0) tableBody.push(['PG Fee', `-Rs. ${pgFee.toLocaleString('en-IN')}`]);

        autoTable(doc, {
          startY: 60,
          theme: 'grid',
          headStyles: { fillColor: [248, 250, 252], textColor: [15, 23, 42] },
          body: tableBody,
        });

        doc.setFontSize(14);
        doc.setFont("Helvetica", "bold");
        if (isDebit) {
          doc.setTextColor(239, 68, 68);
          doc.text(`Total Deducted: -Rs. ${totalFees.toLocaleString('en-IN')}`, 14, (doc as any).lastAutoTable.finalY + 15);
        } else {
          doc.setTextColor(15, 118, 110);
          doc.text(`Net Settlement: Rs. ${netSettlement.toLocaleString('en-IN')}`, 14, (doc as any).lastAutoTable.finalY + 15);
        }
        
        doc.save(`Receipt_${orderId || Date.now()}.pdf`);
      }
    });
  };

  const formatDate = (isoStr: string) => {
    const d = new Date(isoStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="wallet-page">
      <div className="wallet-container fade-in-stagger">
        <div className="wallet-header slide-up-item">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => navigate('/wallet')}
              className="btn-back"
            >
              <ArrowLeft size={24} color="#0f172a" />
            </button>
            <div>
              <h1 className="wallet-title">All Activity</h1>
              <p className="wallet-subtitle">Complete transaction history</p>
            </div>
          </div>
        </div>

        <div className="tx-container slide-up-item" style={{ animationDelay: '0.1s', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: 0, minHeight: 0 }}>
          {loading ? (
            <p style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Loading transactions...</p>
          ) : (
            <>
              {currentMonthString && (
                <div>
                  {/* Month Navigation Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', paddingBottom: '12px', borderBottom: '2px solid #f1f5f9' }}>
                    <button
                      onClick={handleOlderMonth}
                      disabled={currentMonthIndex === availableMonths.length - 1}
                      style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: currentMonthIndex === availableMonths.length - 1 ? '#f8fafc' : 'white', cursor: currentMonthIndex === availableMonths.length - 1 ? 'not-allowed' : 'pointer', color: currentMonthIndex === availableMonths.length - 1 ? '#cbd5e1' : '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                    >
                      <ChevronLeft size={20} />
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Calendar size={20} color="#64748b" />
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#334155', letterSpacing: '1px' }}>
                        {currentMonthString}
                      </h3>
                    </div>

                    <button
                      onClick={handleNewerMonth}
                      disabled={currentMonthIndex === 0}
                      style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: currentMonthIndex === 0 ? '#f8fafc' : 'white', cursor: currentMonthIndex === 0 ? 'not-allowed' : 'pointer', color: currentMonthIndex === 0 ? '#cbd5e1' : '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>

                  {/* Filter Tabs */}
                  <div className="filter-tabs" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom:"10px" }}>
                    {['ALL', 'CREDIT', 'DEBIT', 'PENDING', 'FAILED', 'REFUNDED'].map(type => {
                      const labels: any = { ALL: 'All Transactions', CREDIT: 'Credits', DEBIT: 'Debits', PENDING: 'Pending', FAILED: 'Failed', REFUNDED: 'Refunded' };
                      return (
                      <button
                        key={type}
                        onClick={() => { setFilterType(type); setCurrentPage(1); }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '24px',
                          border: filterType === type ? '1px solid #3b82f6' : '1px solid #e2e8f0',
                          background: filterType === type ? '#eff6ff' : 'white',
                          color: filterType === type ? '#2563eb' : '#64748b',
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {labels[type]}
                      </button>
                      );
                    })}
                  </div>

                  {/* Transaction List */}
                  <div className="tx-list">
                    {currentTransactions.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b', display: 'flex', flexDirection: 'column', alignItems: 'center',justifyContent:"center", gap: '12px',  height:"56vh"}}>
                        <div style={{ background: '#f2f5f8ff', padding: '16px', borderRadius: '50%' }}>
                          <Inbox size={100} color="#d0d5dbff" />
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '16px', color: '#334155', fontWeight: 600 }}>No Activity Found</h4>
                          <p style={{ margin: '4px 0 0', fontSize: '14px' }}>There were no transactions during this month.</p>
                        </div>
                      </div>
                    ) : currentTransactions.map((tx, idx) => {
                      const { amountClass, statusClass, sign, Icon, iconWrapClass, badgeText } = getTxStyles(tx);
                      return (
                      <div 
                        key={tx.id} 
                        className="tx-item slide-up-item"
                        style={{ animationDelay: `${0.2 + (idx * 0.05)}s`, cursor: (tx.commissionDeducted > 0 || tx.commission_GST_Deducted > 0) ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (tx.commissionDeducted > 0 || tx.commission_GST_Deducted > 0) {
                            const productNameMatch = tx.ref.match(/for (.*?) \(Order/);
                            const productName = productNameMatch ? productNameMatch[1] : (tx.orderId ? `Order #${tx.orderId}` : 'Transaction');
                            const grossAmount = tx.type === 'debit' ? (tx.amount || 0) : ((tx.amount || 0) + (tx.commission_GST_Deducted || 0));
                            const netAmt = tx.type === 'debit' ? (tx.amount || 0) : (tx.amount || 0);

                            showReceiptModal(
                              productName,
                              netAmt,
                              tx.commissionDeducted || 0,
                              tx.gstDeducted || 0,
                              tx.tcsDeducted || 0,
                              tx.platformFee || 0,
                              tx.smsCharges || 0,
                              tx.pgFee || 0,
                              netAmt,
                              new Date(tx.date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
                              tx.orderId,
                              tx.type
                            );
                          }
                        }}
                      >
                        <div className="tx-col tx-col-primary">
                          <div className={`tx-icon ${iconWrapClass}`}>
                            <Icon size={20} />
                          </div>
                          <div className="tx-title-group">
                            <h4 className="tx-ref">{tx.ref}</h4>
                            <p className="tx-subtitle">
                              {tx.orderId ? `Order #${tx.orderId} • ` : `Txn ID: ${tx.id.slice(-8).toUpperCase()} • `}{formatDate(tx.date)}
                            </p>
                          </div>
                        </div>

                        <div className="tx-col tx-col-amount">
                          <p className={`tx-amount ${amountClass}`}>
                            {sign}₹{tx.amount.toLocaleString('en-IN')}
                          </p>
                          <span className={`tx-status-badge ${statusClass}`}>
                            {badgeText}
                          </span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Pagination Controls per Month */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: currentPage === 1 ? '#f8fafc' : 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: currentPage === 1 ? '#94a3b8' : '#334155', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, fontSize: '14px', transition: 'all 0.2s' }}
                  >
                    <ChevronLeft size={16} /> Prev
                  </button>

                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#475569' }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: currentPage === totalPages ? '#f8fafc' : 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: currentPage === totalPages ? '#94a3b8' : '#334155', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, fontSize: '14px', transition: 'all 0.2s' }}
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
